import { useEffect, useRef } from "react";

/**
 * CLEAN Page Visibility Auth Hook
 * Simple session refresh on tab return - no loops
 */
export function usePageVisibilityAuth({
  client,
  isAuthenticated = false,
  safetyWindowMs = 120000
}) {
  const busyRef = useRef(false);

  useEffect(() => {
    // Only activate if user is already authenticated
    if (!isAuthenticated) {
      return;
    }

    const handleVisibilityChange = async () => {
      // Only check when page becomes visible
      if (document.visibilityState !== "visible") {
        return;
      }

      // Prevent multiple simultaneous checks
      if (busyRef.current) {
        return;
      }

      busyRef.current = true;

      try {
        console.log("🔄 Tab returned - checking session");
        
        const { data: { session } } = await client.auth.getSession();
        
        // No session = user logged out, nothing to do
        if (!session) {
          console.log("ℹ️ No session - user logged out");
          return;
        }

        // Check if session is expiring soon
        const expiresAt = session.expires_at * 1000;
        const timeLeft = expiresAt - Date.now();
        
        if (timeLeft > safetyWindowMs) {
          console.log("✅ Session still fresh");
          return;
        }

        // Session expiring soon - try refresh
        console.log("🔄 Session expiring soon - refreshing");
        const { error } = await client.auth.refreshSession();
        
        if (error) {
          console.warn("⚠️ Session refresh failed:", error);
          // Let normal auth flow handle this
        } else {
          console.log("✅ Session refreshed successfully");
        }

      } catch (error) {
        console.warn("⚠️ Page visibility check failed:", error);
      } finally {
        busyRef.current = false;
      }
    };

    // Add event listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };

  }, [client, isAuthenticated, safetyWindowMs]);
}
